import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { PaginatedResponse } from '../models/paginatedResponse';
import { CorporateGovernanceSearchRequest } from '../models/api/corporateGovernanceSearchRequest';
import { CorporateGovernanceResultDto } from '../models/api/corporateGovernanceResultDto';
import { CorporateGovernanceProblemTypeDto } from '../models/api/corporateGovernanceProblemTypeDto';
import { CorporateGovernanceProblemType } from '../models/enums/corporateGovernanceProblemType.enum';

@Injectable({
  providedIn: 'root'
})
export class CorporateGovernanceService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/tools/corporate-governance`;

  /**
   * GET /api/tools/corporate-governance/problem-types
   * Lists the corporate-governance problem types and which of them support review.
   */
  getProblemTypes(): Observable<CorporateGovernanceProblemTypeDto[]> {
    return this.http.get<CorporateGovernanceProblemTypeDto[]>(`${this.apiUrl}/problem-types`);
  }

  /**
   * GET /api/tools/corporate-governance/search
   * Search for corporate-governance problem results with pagination
   */
  search(request: CorporateGovernanceSearchRequest): Observable<PaginatedResponse<CorporateGovernanceResultDto>> {
    let params = new HttpParams().set('problemType', request.problemType.toString());

    if (request.problemSubType) {
      params = params.set('problemSubType', request.problemSubType);
    }
    if (request.countryId) {
      params = params.set('countryId', request.countryId);
    }
    if (request.year !== undefined && request.year !== null) {
      params = params.set('year', request.year.toString());
    }
    if (request.hideReviewed) {
      params = params.set('hideReviewed', 'true');
    }
    if (request.pageNumber !== undefined && request.pageNumber !== null) {
      params = params.set('pageNumber', request.pageNumber.toString());
    }
    if (request.pageSize !== undefined && request.pageSize !== null) {
      params = params.set('pageSize', request.pageSize.toString());
    }
    if (request.sortBy) {
      params = params.set('sortBy', request.sortBy);
    }
    if (request.ascending !== undefined && request.ascending !== null) {
      params = params.set('ascending', request.ascending.toString());
    }

    return this.http.get<PaginatedResponse<CorporateGovernanceResultDto>>(`${this.apiUrl}/search`, { params });
  }

  /**
   * GET /api/tools/corporate-governance/export
   * Export filtered corporate-governance results as CSV
   */
  exportCsv(request: CorporateGovernanceSearchRequest): Observable<HttpResponse<Blob>> {
    let params = new HttpParams().set('problemType', request.problemType.toString());

    if (request.problemSubType) {
      params = params.set('problemSubType', request.problemSubType);
    }
    if (request.countryId) {
      params = params.set('countryId', request.countryId);
    }
    if (request.year !== undefined && request.year !== null) {
      params = params.set('year', request.year.toString());
    }
    if (request.hideReviewed) {
      params = params.set('hideReviewed', 'true');
    }

    return this.http.get(`${this.apiUrl}/export`, {
      params,
      responseType: 'blob',
      observe: 'response'
    });
  }

  /**
   * PUT /api/tools/corporate-governance/reviews
   * Marks a reported director as confirmed correct at a company — in every year — or clears it.
   */
  setReviewed(
    problemType: CorporateGovernanceProblemType,
    holderId: string,
    companyId: string,
    reviewed: boolean
  ): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/reviews`, { problemType, holderId, companyId, reviewed });
  }

  /**
   * GET /api/tools/corporate-governance/export-all
   * Export all corporate-governance problem types as CSV
   */
  exportAllCsv(countryId?: string, year?: number): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId);
    }
    if (year !== undefined && year !== null) {
      params = params.set('year', year.toString());
    }

    return this.http.get(`${this.apiUrl}/export-all`, {
      params,
      responseType: 'blob',
      observe: 'response'
    });
  }
}
